import React, { useState, createContext, useContext } from 'react';
export type UserRole = 'authenticated-public-user' | 'customer' | 'dealer' | 'sales-manager' | 'operations-manager' | 'manager-business-unit' | 'ceo' | 'logistic' | 'marketing' | 'financial-manager' | 'system-admin' | 'dealer-admin' | 'region-admin';
export interface UserData {
  role: UserRole;
  name: string;
  email: string;
  companyName: string;
  companyLogo: string | null;
  officialCountry: string;
}
interface UserContextType {
  role: UserRole;
  setRole: (role: UserRole) => void;
  name: string;
  setName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  companyName: string;
  setCompanyName: (name: string) => void;
  companyLogo: string | null;
  setCompanyLogo: (logo: string | null) => void;
  officialCountry: string;
  setOfficialCountry: (country: string) => void;
  // Impersonation
  isImpersonating: boolean;
  originalUser: UserData | null;
  impersonatedUsers: UserData[];
  impersonateUser: (user: UserData) => void;
  removeImpersonatedUser: (email: string) => void;
  returnToOriginal: () => void;
  stopImpersonation: () => void;
}
const UserContext = createContext<UserContextType | undefined>(undefined);
export function UserProvider({
  children
}: {
  children: ReactNode;
}) {
  const [role, setRole] = useState<UserRole>('system-admin'); // Defaulting to system-admin for testing
  const [name, setName] = useState('Daniel Alejandro');
  const [email, setEmail] = useState('daniel.alejandro@aisco.com');
  const [companyName, setCompanyName] = useState('ZEPPELIN');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [officialCountry, setOfficialCountry] = useState('Ukraine');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<UserData | null>(null);
  const [impersonatedUsers, setImpersonatedUsers] = useState<UserData[]>([]);
  const impersonateUser = (user: UserData) => {
    if (!isImpersonating) {
      setOriginalUser({
        role,
        name,
        email,
        companyName,
        companyLogo,
        officialCountry
      });
      setIsImpersonating(true);
    }
    setRole(user.role);
    setName(user.name);
    setEmail(user.email);
    setCompanyName(user.companyName);
    setCompanyLogo(user.companyLogo);
    setOfficialCountry(user.officialCountry);
    setImpersonatedUsers(prev => {
      if (!prev.find(u => u.email === user.email)) {
        return [...prev, user];
      }
      return prev;
    });
  };
  const removeImpersonatedUser = (userEmail: string) => {
    const wasActive = email === userEmail;
    setImpersonatedUsers(prev => prev.filter(u => u.email !== userEmail));
    // If we just removed the user we were impersonating, switch back to original
    if (wasActive && originalUser) {
      setRole(originalUser.role);
      setName(originalUser.name);
      setEmail(originalUser.email);
      setCompanyName(originalUser.companyName);
      setCompanyLogo(originalUser.companyLogo);
      setOfficialCountry(originalUser.officialCountry);
    }
  };
  const returnToOriginal = () => {
    if (!originalUser) return;
    setRole(originalUser.role);
    setName(originalUser.name);
    setEmail(originalUser.email);
    setCompanyName(originalUser.companyName);
    setCompanyLogo(originalUser.companyLogo);
    setOfficialCountry(originalUser.officialCountry);
    // Keep isImpersonating, originalUser, and impersonatedUsers intact
    // so the bar stays visible and badges remain available.
  };
  const stopImpersonation = () => {
    if (originalUser) {
      setRole(originalUser.role);
      setName(originalUser.name);
      setEmail(originalUser.email);
      setCompanyName(originalUser.companyName);
      setCompanyLogo(originalUser.companyLogo);
      setOfficialCountry(originalUser.officialCountry);
    }
    setIsImpersonating(false);
    setOriginalUser(null);
    setImpersonatedUsers([]);
  };
  return <UserContext.Provider value={{
    role,
    setRole,
    name,
    setName,
    email,
    setEmail,
    companyName,
    setCompanyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry,
    isImpersonating,
    originalUser,
    impersonatedUsers,
    impersonateUser,
    removeImpersonatedUser,
    returnToOriginal,
    stopImpersonation
  }} data-id="element-6985">
      {children}
    </UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}